/* The IT-only view of a Claude seat request.
 *
 * Renders nothing unless the API sent an assessment - and the API only sends
 * one to IT staff. That is why there is no role check in here: `assessment`
 * is simply undefined in a requester's response, so the panel disappears
 * without the component ever knowing who is looking.
 *
 * THIS IS NOT THE SECURITY BOUNDARY. The server is. Never move a decision
 * about who sees this into the browser - a hidden element is still in the
 * network tab and the page source.
 */

/* A FALLBACK ONLY. The server sends `recommendationLabel` and that is the
 * source of truth - api/src/shared/seatScore.js - so the email and this page
 * always say the same words. This map exists for a row written before the
 * label was added, and must never become the place the wording is decided. */
const TIER_LABEL = {
  none: 'No account necessary',
  standard: 'Standard seat',
  premium: 'Premium seat',
};

export default function SeatAssessmentPanel({ assessment, turns = [] }) {
  if (!assessment) return null;

  const flags = [
    assessment.needsSecurityReview
      ? ['shield', 'Security review — automations or agents that act on their own'] : null,
    assessment.needsSnowflake
      ? ['database', 'Needs a new Snowflake account — extra setup and a meeting'] : null,
    assessment.needsPaylocity
      ? ['lock', 'Asking for Paylocity (HR/payroll) — restricted role, needs approval'] : null,
    assessment.needsSage
      ? ['lock', 'Asking for Sage (accounting) — restricted role, needs approval'] : null,
    /* Not a risk flag - a cheaper answer. If what they want is something
     * they will use again and again, IT should build it once rather than
     * sell a seat so it can be rebuilt by hand every month. */
    assessment.betterAsItRequest
      ? ['build', 'Wants something they will use again and again — IT should probably BUILD it'] : null,
  ].filter(Boolean);

  return (
    <div className="card card--pad" style={{ marginTop: 20 }}>
      <h3 style={{ marginTop: 0 }}>
        Recommendation:{' '}
        {assessment.recommendationLabel
          || TIER_LABEL[assessment.recommendation]
          || assessment.recommendation}
      </h3>
      <p>{assessment.reasoning}</p>

      {assessment.kenkneeCandidate ? (
        <div className="banner banner--info">
          <span className="material-symbols-outlined" aria-hidden="true">lightbulb</span>
          <span>
            Could be a Kenknee enhancement instead of a paid seat.
            {assessment.kenkneeNote ? ` ${assessment.kenkneeNote}` : ''}
          </span>
        </div>
      ) : null}

      {flags.length ? (
        <ul style={{ marginTop: 12 }}>
          {flags.map(([, text]) => <li key={text}>{text}</li>)}
        </ul>
      ) : null}

      {assessment.connectors?.length ? (
        <p className="muted" style={{ fontSize: 12 }}>
          Connectors asked for: {assessment.connectors.join(', ')}
        </p>
      ) : null}

      <h4>What was asked, and why</h4>
      <ol>
        {turns.map((t) => (
          <li key={t.turnNumber} style={{ marginBottom: 10 }}>
            <strong>{t.question}</strong>
            <br />
            {t.answer || <em className="muted">no answer — they stopped here</em>}
            <br />
            <span className="muted" style={{ fontSize: 12 }}>
              Asked because: {t.branchReason}
              {t.skipped?.length ? ` · Skipped: ${t.skipped.join(', ')}` : ''}
            </span>
          </li>
        ))}
      </ol>

      <p className="muted" style={{ fontSize: 11 }}>
        Advice only — a human grants the seat. Produced by {assessment.modelId}.
      </p>
    </div>
  );
}
